import React, { useState } from "react";
import { Skeleton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import * as S from "./styles";
import {
  getCategory,
  IAnnouncement,
  resolveAnnouncement,
} from "../../utils/announcements";

interface NewsListProps {
  news: IAnnouncement[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  emptyMessage: string;
  onSelect: (announcement: IAnnouncement) => void;
}

interface NewsCardProps {
  announcement: IAnnouncement;
  index: number;
  onSelect: (announcement: IAnnouncement) => void;
}

const NewsCard: React.FC<NewsCardProps> = ({
  announcement,
  index,
  onSelect,
}) => {
  const [loaded, setLoaded] = useState(false);
  const { imageSrc } = resolveAnnouncement(announcement);
  const { label, Icon } = getCategory(announcement.category);

  return (
    <S.NewsCard
      $index={index}
      role="button"
      onClick={() => onSelect(announcement)}
    >
      <S.ImageWrapper>
        {imageSrc ? (
          <>
            {!loaded && (
              <S.ImageSkeleton>
                <Skeleton variant="rectangular" animation="wave" />
              </S.ImageSkeleton>
            )}
            <S.BannerImage
              src={imageSrc}
              alt={announcement.title}
              loading="lazy"
              decoding="async"
              $loaded={loaded}
              onLoad={() => setLoaded(true)}
              onError={() => setLoaded(true)}
            />
          </>
        ) : (
          <S.Placeholder />
        )}
      </S.ImageWrapper>

      <S.CardContent>
        <S.TextGroup>
          <S.CategoryBadge>
            <Icon style={{ fontSize: 13 }} />
            {label}
          </S.CategoryBadge>
          <S.Title>{announcement.title}</S.Title>
          <S.Description>{announcement.description}</S.Description>
        </S.TextGroup>
        <S.Arrow>
          <ChevronRightIcon />
        </S.Arrow>
      </S.CardContent>
    </S.NewsCard>
  );
};

const NewsList: React.FC<NewsListProps> = ({
  news,
  loading,
  error,
  onRetry,
  emptyMessage,
  onSelect,
}) => {
  if (loading && news.length === 0) {
    return (
      <S.Container>
        {[0, 1, 2].map((i) => (
          <S.SkeletonCard key={i}>
            <Skeleton variant="rectangular" height={170} animation="wave" />
            <div style={{ padding: 16 }}>
              <Skeleton width="60%" height={24} animation="wave" />
              <Skeleton width="90%" animation="wave" />
            </div>
          </S.SkeletonCard>
        ))}
      </S.Container>
    );
  }

  if (error && news.length === 0) {
    return (
      <S.Feedback>
        <S.InfoText>Não foi possível carregar os anúncios.</S.InfoText>
        <S.RetryButton type="button" onClick={onRetry}>
          Tentar novamente
        </S.RetryButton>
      </S.Feedback>
    );
  }

  if (news.length === 0) {
    return (
      <S.Feedback>
        <S.InfoText>{emptyMessage}</S.InfoText>
      </S.Feedback>
    );
  }

  return (
    <S.Container>
      {news.map((announcement, index) => (
        <NewsCard
          key={announcement.id}
          announcement={announcement}
          index={index}
          onSelect={onSelect}
        />
      ))}
    </S.Container>
  );
};

export default NewsList;
